// platform-versions.ts — (tf3-27; creative C1.6b handoff §1.1 / §2.1) the Finish & Post panel's versions view, kept pure so
// its rules are tested on their own. A finishing run makes one version per platform (TikTok, Instagram Reels, YouTube
// Shorts), each measured by platform_verify; a refused version holds the delivery's automatic approval, and stays refused
// when the person approves it: it is never delivered, never posted.

/** GET …/post-production/runs/:runId/versions (the workers' answer, passed through). */
export interface VersionCheck { checkId: string; passed: boolean; measured: string | null; expected: string | null; detail: string }
export interface PlatformVersion {
  platform: string; status: string; refusal: string | null; url: string | null; profileVersion: string | null;
  /** (tf3-44; C2.3-2 §3) TikTok's no-music version ("no_music"); null or absent: the full version */
  audio?: "no_music" | null;
  durationSec: number | null; cover: { ms: number; reason: string | null } | null; checks: VersionCheck[];
  words: { caption: string | null; hashtags: string[]; title: string | null; postBody: string | null; tiktokTitlePrefill?: string;
    /** (tf3-29; C1.6b-2b) publish_verify's verdict on this platform's copy, and why when refused (absent: an older workers) */
    copyStatus?: "passed" | "refused" | "unchecked"; copyRefusal?: string | null } | null;
  /** (tf3-29; C1.6b-2b) the platform grade, recorded only (null: not graded yet, or the setting off) */
  grade?: PlatformGrade | null;
}
export interface PlatformGrade {
  status: "graded" | "not_graded"; model: string | null; calibration: string; detail: string;
  criteria: { criterion: string; score: number; reason: string }[];
}
export interface VersionsView {
  finishingRunId: string; productionRunId: string; delivery: "none" | "awaiting" | "approved" | "rejected";
  master: { url: string | null; status: string } | null; versions: PlatformVersion[];
}

export const PLATFORM_LABEL: Record<string, string> = { tiktok: "TikTok", instagram_reels: "Instagram Reels", youtube_shorts: "YouTube Shorts" };
export const platformLabel = (p: string) => PLATFORM_LABEL[p] ?? p;
/** (tf3-44) TikTok lists two versions: the no-music one is labelled and keyed apart from the full one */
export const NO_MUSIC_LABEL = "TikTok, no music";
export const versionLabel = (v: { platform: string; audio?: string | null }) =>
  v.audio === "no_music" ? (v.platform === "tiktok" ? NO_MUSIC_LABEL : `${platformLabel(v.platform)}, no music`) : platformLabel(v.platform);
export const versionKey = (v: { platform: string; audio?: string | null }) => `${v.platform}:${v.audio ?? "full"}`;

/** A version's state, in words: made (produced), approved (ready), refused (failed, with its reason). */
export function versionState(v: Pick<PlatformVersion, "status" | "refusal">): { text: string; refused: boolean } {
  switch (v.status) {
    case "ready": return { text: "Approved", refused: false };
    case "produced": return { text: "Made — waiting for approval", refused: false };
    case "failed": return { text: `Refused: ${v.refusal || "no reason was given"}`, refused: true };
    default: return { text: v.status, refused: false };
  }
}

/** (tf3-33) the master's state, in the versions' words (it is never refused: it is delivered as it is). */
export function masterState(m: NonNullable<VersionsView["master"]>): string {
  return m.status === "ready" ? "Approved" : m.status === "produced" ? "Made — waiting for approval" : m.status === "failed" ? "Failed" : m.status;
}
export const MASTER_NOTE = "Delivered as it is; the versions are cut from it.";

/** Above the approve button while the delivery is still to decide and any version is refused (the handoff's words). */
export const REFUSED_WARNING = "Approving delivers the master and the versions that passed. The refused version(s) stay refused and won't be posted.";
export const refusedWarning = (view: VersionsView | null): string | null =>
  view && view.delivery !== "approved" && view.delivery !== "rejected" && view.versions.some((v) => versionState(v).refused) ? REFUSED_WARNING : null;

/** "0:31" — a measured length; unknown → "—". */
export function lengthText(sec: number | null): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return "—";
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
/** The cover frame: its time in the video, and why it was chosen. */
export const coverText = (c: PlatformVersion["cover"]): string =>
  !c ? "the platform's default frame" : `at ${(c.ms / 1000).toFixed(1)} s${c.reason ? ` — ${c.reason}` : ""}`;
/** A check as measured: "duration: 31.2 (expected ≤ 600)". */
export const checkText = (c: VersionCheck): string =>
  `${c.checkId}: ${c.measured ?? "—"}${c.expected ? ` (expected ${c.expected})` : ""}${!c.passed && c.detail ? ` — ${c.detail}` : ""}`;
/** The words as they will post: the post body, plus YouTube's own title. */
export function wordsLines(v: PlatformVersion): { label: string; text: string }[] {
  if (!v.words) return [];
  const out: { label: string; text: string }[] = [];
  if (v.platform === "youtube_shorts" && v.words.title) out.push({ label: "Title", text: v.words.title });
  out.push({ label: "Post", text: v.words.postBody ?? "—" });
  return out;
}

/** The platform choice (the run's `params.platform`): which version to make; none → the engine's own targets. No long-form
 *  YouTube: the engine makes no long-form version. */
export const VERSION_PLATFORMS: Array<{ code: string; label: string }> = [
  { code: "", label: "— every platform it's posted to —" },
  { code: "tiktok", label: "TikTok" },
  { code: "instagram_reels", label: "Instagram Reels" },
  { code: "youtube_shorts", label: "YouTube Shorts" },
];
export const VERSIONS_NOTE = "Versions are made for each platform the video is posted to. Choose one to make only that one.";

/** (tf3-29) the copy's verdict beside its words: a refused copy's channel won't post until it is fixed and finishing re-run. */
export function copyVerdict(w: PlatformVersion["words"]): { text: string; refused: boolean } | null {
  if (!w || !w.copyStatus) return null;
  if (w.copyStatus === "passed") return { text: "Copy checked: passed", refused: false };
  if (w.copyStatus === "refused")
    return { text: `Copy refused: ${(w.copyRefusal || "").replace(/^refused by publish_verify:\s*/, "") || "no reason was given"} — this channel won't post until the copy is fixed and finishing is run again`, refused: true };
  return { text: "Copy not checked (approved before the check existed)", refused: false };
}

const CRITERION_LABEL: Record<string, string> = {
  caption_fits_platform: "Caption fits the platform", caption_fits_video: "Caption fits the video", cover_reads_at_thumbnail: "Cover reads at thumbnail size",
};
const CRITERION_ORDER = ["caption_fits_platform", "caption_fits_video", "cover_reads_at_thumbnail"];
/** (tf3-29) the grade: each score (0–1) with its reason, and the calibration shown small (uncalibrated until TF-7: a hint,
 *  not a verdict). Not graded → its detail. None → nothing. */
export function gradeView(g: PlatformGrade | null | undefined): { lines: { label: string; score: string; reason: string }[]; note: string } | null {
  if (!g) return null;
  if (g.status !== "graded") return { lines: [], note: `Not graded: ${g.detail || "no reason was given"}` };
  const rank = (c: string) => { const i = CRITERION_ORDER.indexOf(c); return i < 0 ? CRITERION_ORDER.length : i; };
  return {
    lines: [...g.criteria].sort((a, b) => rank(a.criterion) - rank(b.criterion))
      .map((c) => ({ label: CRITERION_LABEL[c.criterion] ?? c.criterion, score: Number.isFinite(c.score) ? c.score.toFixed(2) : "—", reason: c.reason })),
    note: `${g.calibration} — a hint, not a verdict`,
  };
}
